import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Layout } from './components/layout/Layout';
import { ToastContainer } from './components/ui/ToastContainer';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { useAuthStore } from './stores/authStore';
import './locales/i18n';

// Auto-reload on chunk load failure (stale deployment)
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch(() => {
      const reloaded = sessionStorage.getItem('chunk-reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
      }
      return factory();
    })
  );
}

// Lazy-loaded pages for code splitting
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const ProjectHomePage = lazyWithRetry(() => import('./pages/ProjectHomePage').then(m => ({ default: m.ProjectHomePage })));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ProjectsPage = lazyWithRetry(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectCreatePage = lazyWithRetry(() => import('./pages/ProjectCreatePage').then(m => ({ default: m.ProjectCreatePage })));
// ProjectDetailPage removed — merged into ProjectHomePage. Route redirects via ProjectRedirect.
import { ProjectRedirect } from './components/routing/ProjectRedirect';
const StepExecutionPage = lazyWithRetry(() => import('./pages/StepExecutionPage').then(m => ({ default: m.StepExecutionPage })));
const MyTasksPage = lazyWithRetry(() => import('./pages/MyTasksPage').then(m => ({ default: m.MyTasksPage })));
const GanttPage = lazyWithRetry(() => import('./pages/GanttPage').then(m => ({ default: m.GanttPage })));
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const ReportsPage = lazyWithRetry(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const FlowManagementPage = lazyWithRetry(() => import('./pages/FlowManagementPage').then(m => ({ default: m.FlowManagementPage })));
const FlowDetailPage = lazyWithRetry(() => import('./pages/FlowDetailPage').then(m => ({ default: m.FlowDetailPage })));
const UserManagementPage = lazyWithRetry(() => import('./pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SystemSettingsPage = lazyWithRetry(() => import('./pages/SystemSettingsPage').then(m => ({ default: m.SystemSettingsPage })));
const ClientManagementPage = lazyWithRetry(() => import('./pages/ClientManagementPage').then(m => ({ default: m.ClientManagementPage })));
const RegisterPage = lazyWithRetry(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const HelpPage = lazyWithRetry(() => import('./pages/HelpPage').then(m => ({ default: m.HelpPage })));
const WbsPage = lazyWithRetry(() => import('./pages/WbsPage').then(m => ({ default: m.WbsPage })));
const WbsTemplateManagementPage = lazyWithRetry(() => import('./pages/WbsTemplateManagementPage').then(m => ({ default: m.WbsTemplateManagementPage })));
const IssuesPage = lazyWithRetry(() => import('./pages/IssuesPage').then(m => ({ default: m.IssuesPage })));
const BacklogImportPage = lazyWithRetry(() => import('./pages/BacklogImportPage').then(m => ({ default: m.BacklogImportPage })));
const ManagerDashboardPage = lazyWithRetry(() => import('./pages/ManagerDashboardPage').then(m => ({ default: m.ManagerDashboardPage })));
const MonthlyReportPage = lazyWithRetry(() => import('./pages/MonthlyReportPage').then(m => ({ default: m.MonthlyReportPage })));
const BacklogSettingsPage = lazyWithRetry(() => import('./pages/BacklogSettingsPage').then(m => ({ default: m.BacklogSettingsPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="text-gray-400 text-sm">読み込み中...</div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ApproverRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'member') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { fetchMe, user } = useAuthStore();
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    fetchMe().finally(() => setInitializing(false));
  }, [fetchMe]);

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="home" element={<ProjectHomePage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/new" element={<ApproverRoute><ProjectCreatePage /></ApproverRoute>} />
            <Route path="projects/:id" element={<ProjectRedirect />} />
            <Route path="projects/:id/wbs" element={<WbsPage />} />
            <Route path="projects/:id/steps/:stepId" element={<StepExecutionPage />} />
            <Route path="issues" element={<IssuesPage />} />
            <Route path="my-tasks" element={<MyTasksPage />} />
            <Route path="gantt" element={<GanttPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="reports" element={<ApproverRoute><ReportsPage /></ApproverRoute>} />
            <Route path="admin/flows" element={<ApproverRoute><FlowManagementPage /></ApproverRoute>} />
            <Route path="admin/flows/:id" element={<ApproverRoute><FlowDetailPage /></ApproverRoute>} />
            <Route path="admin/clients" element={<ApproverRoute><ClientManagementPage /></ApproverRoute>} />
            <Route path="admin/wbs-templates" element={<ApproverRoute><WbsTemplateManagementPage /></ApproverRoute>} />
            <Route path="admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            <Route path="admin/settings" element={<AdminRoute><SystemSettingsPage /></AdminRoute>} />
            <Route path="admin/import" element={<AdminRoute><BacklogImportPage /></AdminRoute>} />
            <Route path="dashboard/manager" element={<ApproverRoute><ManagerDashboardPage /></ApproverRoute>} />
            <Route path="reports/monthly" element={<ApproverRoute><MonthlyReportPage /></ApproverRoute>} />
            <Route path="settings/backlog" element={<AdminRoute><BacklogSettingsPage /></AdminRoute>} />
            <Route path="settings" element={<SettingsPage />} />

            <Route path="help" element={<HelpPage />} />
          </Route>
        </Routes>
      </Suspense>
      <ToastContainer />
    </BrowserRouter>
    </ErrorBoundary>
  );
}
